import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';

import { K8sProvider } from '@cast/shared-types';
import { Cluster } from '@cast/types';
import { usePrevious } from '@cast/utils';

import { useClustersQuery } from '@cast/shared-cluster-context-data-access';

import { getReconnectCluster } from '@oneclick/utils/cluster/getReconnectCluster';

type Provider = K8sProvider | 'all';

type UseGetNewlyOnboardedClustersArgs = {
  provider?: Provider;
  polling: boolean;
  clusterScanTimeFrom?: Date;
  enabled?: boolean;
};

export const useGetNewlyOnboardedClusters = ({
  provider = 'all',
  polling,
  clusterScanTimeFrom: explicitlyProvidedClusterScanTimeFrom,
  enabled = true,
}: UseGetNewlyOnboardedClustersArgs) => {
  const [clusterScanTimeFrom] = useState(() =>
    dayjs(explicitlyProvidedClusterScanTimeFrom)
  );

  const previousClustersMap = useRef<
    Partial<Record<Provider, typeof clusters>>
  >({});

  const { clusters, refetchClusters } = useClustersQuery({
    polling,
    enabled,
  });

  previousClustersMap.current[provider] = usePrevious<Cluster[]>(
    clusters as Cluster[]
  );
  const previousClusters = previousClustersMap.current[provider];
  const [newConnections, setNewConnections] = useState<Cluster[]>([]);

  useEffect(() => {
    setNewConnections([]);
    if (previousClusters) {
      delete previousClustersMap.current[provider];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  useEffect(() => {
    if (!clusters) {
      return;
    }

    const newClusters = clusters.filter(
      (cluster) => clusterScanTimeFrom.diff(dayjs(cluster.createdAt)) <= 0
    );

    let reconnectedClusters: Cluster[] = [];
    if (previousClusters) {
      reconnectedClusters = getReconnectCluster(previousClusters, clusters);
    }

    setNewConnections((current) => {
      const allNewlyConnectedClusters = [
        ...newClusters,
        ...reconnectedClusters,
      ];
      const allNewlyConnectedClustersMap = new Map<Cluster['id'], Cluster>();
      allNewlyConnectedClusters.forEach((cluster) => {
        allNewlyConnectedClustersMap.set(cluster.id, cluster);
      });

      const updatedCurrent = current.map(
        (cluster) => allNewlyConnectedClustersMap.get(cluster.id) || cluster
      );

      return uniqBy(
        [...updatedCurrent, ...reconnectedClusters, ...newClusters],
        'id'
      ).filter(
        (cluster) => provider === 'all' || cluster.providerType === provider
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusters, provider, previousClusters]);

  return {
    newConnections,
    refetchClusters,
  };
};
